import { todayDateRangeInUserTimeZone } from '@/helpers';
import { cloneDeep } from 'lodash-es';
import { BetDateType } from '@/constants';
import { applyDatePeriodPresetToBetDate } from '@/helpers/applyDatePeriodPresetToBetDate';
import { BotUsageStatsItem } from '@/models/botUsageStats';
import { createBotUsageStatsTableHeaders } from '@/pages/admin/botUsageStats/BotUsageStatsPageHeaders';
import { isRequestCanceled } from '@/services/baseService';
import { botService } from '@/services/botService';
import { createLoadingGenerationTracker } from '@/stores/composables/createLoadingGenerationTracker';
import { createStorePersistConfig } from '@/stores/composables/createStorePersistConfig';
import { useFilterPanelState } from '@/stores/composables/useFilterPanelState';
import { useFilterPersistence } from '@/stores/composables/useFilterPersistence';
import { IListParams, IPageOptions } from '@/types';
import { BotUsageStatsSection, IBotUsageStatsFilters, IBotUsageStatsRowApi } from '@/types/botUsageStats';
import type { DataTableHeader } from '@/types/vuetify';

const SECTIONS: BotUsageStatsSection[] = ['betType', 'stakeType', 'options', 'conditions'];

function mapRows(rows: IBotUsageStatsRowApi[] | undefined): BotUsageStatsItem[] {
    if (!Array.isArray(rows)) return [];
    return rows.map((row) => new BotUsageStatsItem(row));
}

function createBotUsageStatsStoreSetup(storeId: string) {
    // --- state ---
    const itemsBySection = reactive<Record<BotUsageStatsSection, BotUsageStatsItem[]>>({
        betType: [],
        stakeType: [],
        options: [],
        conditions: [],
    });

    const loading = ref(false);
    const loadGeneration = createLoadingGenerationTracker();

    // --- filters ---
    const filters = reactive<IBotUsageStatsFilters>({
        betDateType: {
            value: BetDateType.SettlementGMT,
            title: 'bets.betDateType',
            allowSaveState: true,
            disableClear: true,
        },
        datePeriod: {
            value: 'today',
            title: 'bookBalance.datePeriod.title',
            allowSaveState: true,
            disableClear: true,
        },
        betDate: {
            value: todayDateRangeInUserTimeZone(),
            title: 'bookBalance.betDateFilter',
            allowSaveState: false,
            disableClear: true,
        },
    });

    // --- filter panel ---
    const { hiddenFilters, visibleFiltersMobile, filterOrder, availableFilters, useButtonForFiltersApply, saveFiltersState, visibleFilters } =
        useFilterPanelState(filters);

    // --- options ---
    const options = ref<IPageOptions>({
        page: 1,
        itemsPerPage: 500,
        rowsPerPageItems: [500],
        sortBy: [],
    });

    // --- reset ---
    function resetFilters() {
        filters.betDateType.value = BetDateType.SettlementGMT;
        filters.datePeriod.value = 'today';
        filters.betDate.value = todayDateRangeInUserTimeZone();
    }

    // --- table ---
    const headers = computed<DataTableHeader[]>(() => createBotUsageStatsTableHeaders());
    const availableColumns = computed(() => headers.value.filter((header) => header.title));
    const hiddenColumns = ref<string[]>([]);
    const tableHeaders = computed<DataTableHeader[]>(() =>
        headers.value.filter((header) => header.key && (!hiddenColumns.value.includes(header.key) || !header.title))
    );

    // --- persistence ---
    const { reloadFiltersFromStorage } = useFilterPersistence({
        storeId,
        primaryStoreId: 'botUsageStats',
        filters,
        saveFiltersState,
        resetFilters,
    });

    // --- actions ---
    function clearSectionItems() {
        for (const section of SECTIONS) {
            itemsBySection[section] = [];
        }
    }

    function buildListParams(): IListParams<IBotUsageStatsFilters> {
        const filtersCopy = cloneDeep(filters);
        applyDatePeriodPresetToBetDate(filtersCopy);
        filtersCopy.datePeriod.value = null;

        return {
            filters: filtersCopy,
            options: {
                sortBy: options.value.sortBy,
                page: options.value.page,
                itemsPerPage: options.value.itemsPerPage,
            },
            requiredData: [],
            requiredDictionaries: [],
        };
    }

    async function loadData() {
        const loadId = loadGeneration.next();
        loading.value = true;
        try {
            const response = await botService.usageStats(buildListParams());
            if (!loadGeneration.isLatest(loadId)) return;

            itemsBySection.betType = mapRows(response.data.betType);
            itemsBySection.stakeType = mapRows(response.data.stakeType);
            itemsBySection.options = mapRows(response.data.options);
            itemsBySection.conditions = mapRows(response.data.conditions);
        } catch (e) {
            if (isRequestCanceled(e)) return;
            if (!loadGeneration.isLatest(loadId)) return;
            clearSectionItems();
            throw e;
        } finally {
            if (loadGeneration.isLatest(loadId)) {
                loading.value = false;
            }
        }
    }

    function updateOptions(value: Partial<IPageOptions>) {
        if (value.page !== undefined) {
            options.value.page = value.page;
        }
        if (value.itemsPerPage !== undefined) {
            options.value.itemsPerPage = value.itemsPerPage;
        }
    }

    function clearData() {
        loadGeneration.next();
        loading.value = false;
        clearSectionItems();
        options.value.page = 1;
    }

    // --- return ---
    return {
        storeId,
        itemsBySection,
        filters,
        hiddenFilters,
        visibleFiltersMobile,
        filterOrder,
        availableFilters,
        useButtonForFiltersApply,
        saveFiltersState,
        visibleFilters,
        resetFilters,
        reloadFiltersFromStorage,
        headers,
        tableHeaders,
        availableColumns,
        hiddenColumns,
        loading,
        options,
        loadData,
        updateOptions,
        clearData,
        stats: undefined,
        hasExport: false,
        hasRefresh: true,
    };
}

export function useBotUsageStatsStore(storeId: string = 'botUsageStats') {
    const useStore = defineStore(storeId, () => createBotUsageStatsStoreSetup(storeId), {
        persist: createStorePersistConfig(storeId, 'botUsageStats', []),
    });
    return useStore();
}

export type BotUsageStatsStoreSetup = ReturnType<typeof useBotUsageStatsStore>;
